import { ruleColor } from './ruleConfig.js';

/**
 * Extract a saveable snapshot from the live Cytoscape instance + React state.
 * Maps layerEdges React state to the ArchitectureRule shape for the backend.
 */
export function extractCanvasSnapshot(cy, layerNodes, layerEdges, collapsedLayers, nodeParentMapRef, suggestedLayerIds = new Set(), collapsedNodePositions = {}) {
  if (!cy) return null;

  const nodePositions = [];
  cy.nodes("[!isLayer]").forEach(node => {
    const pos = node.position();
    // Skip nodes whose only parent is a not-yet-confirmed suggestion
    const parentId = nodeParentMapRef[node.id()] ?? null;
    if (parentId && suggestedLayerIds.has(parentId)) return;
    nodePositions.push({
      nodeId:        node.id(),
      x:             pos.x,
      y:             pos.y,
      parentLayerId: parentId,
    });
  });

  // Exclude suggested (unconfirmed) layers from the persisted snapshot
  const layers = layerNodes.filter(l => !suggestedLayerIds.has(l.id)).map(l => {
    const cyNode = cy.$(`#${l.id}`);
    const pos    = cyNode.length ? cyNode.position() : { x: 0, y: 0 };
    return {
      id:        l.id,
      name:      l.name,
      color:     l.color,
      x:         pos.x,
      y:         pos.y,
      width:     l.w  ?? 200,
      height:    l.h  ?? 150,
      nodeIds:   Object.entries(nodeParentMapRef)
                   .filter(([, lid]) => lid === l.id)
                   .map(([nid]) => nid),
      collapsed: collapsedLayers.has(l.id),
    };
  });

  // Collapsed children are absent from Cytoscape. Persist their cached offsets
  // relative to the current layer centre, including after a collapsed layer moves.
  const visibleIds = new Set(nodePositions.map(p => p.nodeId));
  layers.filter(l => l.collapsed).forEach(layer => {
    Object.entries(collapsedNodePositions[layer.id] ?? {}).forEach(([nodeId, offset]) => {
      if (visibleIds.has(nodeId) || nodeParentMapRef[nodeId] !== layer.id) return;
      nodePositions.push({ nodeId, x: layer.x + offset.x, y: layer.y + offset.y, parentLayerId: layer.id });
    });
  });

  const pan = cy.pan();

  // Map layerEdges React state → ArchitectureRule shape
  const rules = layerEdges.map(e => ({
    id:            e.id,
    sourceLayerId: e.source,
    targetLayerId: e.target,
    ruleType:      e.ruleType      ?? "dependency",
    enforcement:   e.enforcement   ?? (e.rule === "not_allowed" ? "denied" : e.rule) ?? "denied",   // legacy shim: old "not_allowed" maps to "denied". Final fallback is "denied" (the model default) for edges missing enforcement entirely — "allowed" is not a valid enforcement and the backend's choices constraint rejects it.
    name:          e.name          ?? "",
    description:   e.description   ?? "",
    isActive:      e.isActive      ?? true,
    color:         e.color         ?? "",
    createdById:   e.createdById   ?? "",
    createdAt:     e.createdAt     ?? new Date().toISOString(),
  }));

  return {
    layers,
    nodePositions,
    rules,
    viewport: { pan: { x: pan.x, y: pan.y }, zoom: cy.zoom() },
  };
}


/**
 * Prepare React-owned elements and return a position/viewport restoration callback.
 * Run the callback in a layout effect AFTER react-cytoscapejs reconciles elements.
 * Adding layers here would make reconciliation skip existing IDs, leaving stale
 * labels and collapse state on those elements.
 */
export function hydrateCanvasFromSnapshot(
  cy, snapshot, setLayerNodes, setLayerEdges, setCollapsedLayers,
  nodeParentMapRef, collapsedNodePositionsRef, COLLAPSED_W, COLLAPSED_H,
) {
  if (!cy || !snapshot) return;
  const canvas = snapshot.canvas ?? snapshot;
  const { layers = [], nodePositions = [], rules = [], viewport } = canvas;
  const collapsedSet = new Set(layers.filter(l => l.collapsed).map(l => l.id));
  const savedById = new Map(nodePositions.map(p => [p.nodeId, p]));

  Object.keys(nodeParentMapRef).forEach(k => delete nodeParentMapRef[k]);
  Object.keys(collapsedNodePositionsRef).forEach(k => delete collapsedNodePositionsRef[k]);
  const positions = new Map();
  layers.forEach(layer => {
    const offsets = {};
    const ids = layer.nodeIds ?? [];
    const columns = Math.ceil(Math.sqrt(ids.length));
    ids.forEach((id, index) => {
      nodeParentMapRef[id] = layer.id;
      // Older collapsed snapshots omitted child positions. Spread those members
      // deterministically inside their saved component instead of stacking at 0,0.
      const saved = savedById.get(id);
      const position = saved ?? {
        x: layer.x + (index % columns - (columns - 1) / 2) * 82,
        y: layer.y + (Math.floor(index / columns) - (Math.ceil(ids.length / columns) - 1) / 2) * 82,
      };
      positions.set(id, position);
      offsets[id] = { x: position.x - layer.x, y: position.y - layer.y };
    });
    if (layer.collapsed) collapsedNodePositionsRef[layer.id] = offsets;
  });
  nodePositions.forEach(p => positions.set(p.nodeId, p));

  // Map saved rules back to React edges, including backward-compatible aliases.
  setLayerNodes(
    layers.map(l => ({ id: l.id, name: l.name, color: l.color, w: l.width, h: l.height }))
  );
  setLayerEdges(
    rules.map(r => ({
      id:          r.id,
      source:      r.sourceLayerId,
      target:      r.targetLayerId,
      color:       r.color || _defaultColorForEnforcement(r.enforcement),
      // backward-compat alias — existing UI reads e.rule
      rule:        r.enforcement === "not_allowed" ? "denied" : (r.enforcement ?? "denied"),
      enforcement: r.enforcement === "not_allowed" ? "denied" : (r.enforcement ?? "denied"),
      ruleType:    r.ruleType,
      name:        r.name,
      description: r.description,
      isActive:    r.isActive,
      createdById: r.createdById,
      createdAt:   r.createdAt,
    }))
  );
  setCollapsedLayers(collapsedSet);

  return () => {
    if (cy.destroyed()) return;
    cy.batch(() => {
      positions.forEach((position, id) => {
        const node = cy.getElementById(id);
        if (node.length) node.position({ x: position.x, y: position.y });
      });
      layers.forEach(layer => {
        const node = cy.getElementById(layer.id);
        if (!node.length) return;
        // Expanded compounds derive their centre from their children. Empty and
        // collapsed components need their explicit saved position restored.
        if (!node.children().length) node.position({ x: layer.x, y: layer.y });
        node.style(collapsedSet.has(layer.id)
          ? { width: COLLAPSED_W, height: COLLAPSED_H }
          : { width: layer.width, height: layer.height });
      });
    });
    if (viewport) cy.viewport({ pan: { ...viewport.pan }, zoom: viewport.zoom });
  };
}


function _defaultColorForEnforcement(enforcement) {
  return ruleColor(enforcement);
}
